<template>
  <LoadingWrapper :state="status">
    <div class="mb-4 flex flex-row items-start justify-between">
      <div v-if="analysisData" class="flex flex-row gap-1">
        <div class="flex flex-col -space-y-1">
          <div
            v-for="entry in analysisData.perspectives"
            v-show="entry.numberOfFeedbacks > 0"
            :key="entry.key"
            class="flex flex-row items-center"
          >
            <span
              class="text-body-large text-primary mr-2 inline-block w-6 text-right font-semibold"
            >
              {{ entry.numberOfFeedbacks }}
            </span>
            <span v-if="entry.key === SELF_PERSPECTIVE_KEY" class="text-body-medium">
              {{ $t('perspectives.self-long', { count: entry.numberOfFeedbacks }) }}
            </span>
            <template v-else>
              <span class="text-body-medium">
                {{ $t('common.feedback', { count: entry.numberOfFeedbacks }) }}
                <span class="lowercase">{{ $t('common.from') }}</span>
                {{ entry.numberOfSenders }}
                {{ perspectiveLabel(entry.key, entry.numberOfSenders) }}
              </span>
              <BaseMemberAvatars
                v-if="entry.key === firstNonSelfKey && nonSelfMembers.length > 0"
                size="24"
                :members="nonSelfMembers"
                class="bg-surface pl-1"
              />
            </template>
          </div>
        </div>
      </div>
      <BaseTextButton
        v-if="data.surveys.length > 0"
        icon="filter_list"
        shape="square"
        color="outlined"
        @click="openSurveySelectorDialog"
        >{{ selectedSurveyIds.length }}
        {{ $t('common.survey', { count: selectedSurveyIds.length }) }}</BaseTextButton
      >
      <span v-else class="text-label-medium text-on-surface-variant">
        {{ $t('profile.noSurveys') }}
      </span>

      <SurveySelectorDialog
        :subject="user"
        :title="`Umfragen zu ${data.title}`"
        :model-value="dialogSelectedSurveyIds"
        :is-open="isOpen"
        :all-surveys="[...data.surveys]"
        :topic-perspectives="topicPerspectives"
        @update:model-value="handleSurveySelection"
        @close-dialog="closeDialog"
      />
    </div>
    <PulseProfileAnalysis
      v-if="analysisData"
      :subject="user"
      :questions="analysisData.questions"
      :texts="analysisData.texts"
      :topic-perspectives="topicPerspectives"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import PulseProfileAnalysis from '@/core/components/analytics/PulseProfileAnalysis.vue'
  import { usePulseProfile } from '../composables/use-pulse-profile'
  import SurveySelectorDialog from '../../../core/components/survey-selector/SurveySelectorDialog.vue'
  import { computed, ref } from 'vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useUserStore } from '@/stores/user.store'
  import { SELF_PERSPECTIVE_KEY, perspectivesFromTopic } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'

  interface Props {
    feedbackTopicId: string
  }

  const { feedbackTopicId } = defineProps<Props>()

  const { user } = useUserStore()

  const {
    data,
    status,
    isOpen,
    closeDialog,
    selectedSurveyIds,
    openDialog,
    applySelectedSurveys,
    analysisData,
  } = usePulseProfile(feedbackTopicId)

  const dialogSelectedSurveyIds = ref<string[]>([])
  const { t } = useI18n()

  const topicPerspectives = computed(() =>
    data.value ? perspectivesFromTopic(data.value) : [],
  )
  const nonSelfMembers = computed(() => {
    return analysisData.value?.feedbackSenders.filter((sender) => sender.id !== user.id) ?? []
  })

  const firstNonSelfKey = computed(() => {
    return analysisData.value?.perspectives.find(
      (entry) => entry.key !== SELF_PERSPECTIVE_KEY && entry.numberOfFeedbacks > 0,
    )?.key
  })

  const perspectiveLabel = (key: string, count: number) =>
    resolvePerspectiveLabel(key, topicPerspectives.value, {
      kind: 'nameLong',
      count,
      selfLabel: t('perspectives.self'),
    })

  const openSurveySelectorDialog = () => {
    dialogSelectedSurveyIds.value = [...selectedSurveyIds.value]
    openDialog()
  }

  const handleSurveySelection = (surveyIds: string[]) => {
    dialogSelectedSurveyIds.value = [...surveyIds]
    applySelectedSurveys(surveyIds)
  }
</script>
